import React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { I } from "../redesign/kvpIcons";
import { BereichAufnahme } from "./screenshot/BereichAufnahme";
import { MarkierEditor } from "./screenshot/MarkierEditor";
import { Selbstausloeser, SELBSTAUSLOESER_SEKUNDEN } from "./screenshot/Selbstausloeser";
import { BEREICH_STANDARD, bereichAusRoute } from "./bereichAusRoute";
import { ansichtAusDom } from "./ansichtErfassen";
import { spurenSchnappschuss } from "./ticketSpuren";
import { sammleTelefonieStand } from "./telefonieDiagnose";
import { erfasseStation, verlaufSchnappschuss } from "./seitenVerlauf";
import { AnhangEntwurf, ticketService } from "./ticketService";
import { BEREICH_LABEL, bereichAuswahlLabel, bereichOptionen, TicketArt, TicketBereich, TicketTechnik } from "./ticketTypen";
import { pruefeAnhaenge } from "./anhangRegeln";
import { istLeer, ladeEntwurf, merkeEntwurf, verwirfEntwurf } from "./ticketEntwurf";
import "./tickets.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export type MeldeKontext = {
  route: string | null; seitentitel: string | null; bereich: TicketBereich;
  /** Aktiver Tab / Ansicht / oberstes Fenster (ansichtAusDom), z. B. „Tab: Dokumente“. */
  ansicht?: string | null;
  bezugTyp?: string; bezugId?: string; bezugLabel?: string; technik: TicketTechnik | null;
};

/** Kontext SOFORT beim Klick einsammeln — bevor sich die Seite verändert. */
export function kontextJetzt(pfad: string): MeldeKontext {
  const treffer = bereichAusRoute(pfad);
  const titel = typeof document !== "undefined" ? document.title || null : null;
  const ansicht = typeof document !== "undefined" ? ansichtAusDom(document.body) : null;
  erfasseStation(); // der Ort des Meldens ist die letzte Station
  return { route: pfad, seitentitel: titel, ansicht, ...treffer, technik: { ...spurenSchnappschuss(), verlauf: verlaufSchnappschuss() } };
}

type Props = { kontext: MeldeKontext | null; onSchliessen: () => void; onGesendet: (id: number) => void };
type Phase = "dialog" | "ausloeser" | "aufnahme" | "markieren";

export function MeldeDialog({ kontext, onSchliessen, onGesendet }: Props) {
  // Entwurf vom letzten Öffnen: Schließen verwirft nichts (ticketEntwurf.ts).
  const [entwurf] = React.useState(ladeEntwurf);
  const [art, setArt] = React.useState<TicketArt>(entwurf.art);
  const [betreff, setBetreff] = React.useState(entwurf.betreff);
  const [inhalt, setInhalt] = React.useState(entwurf.inhalt);
  // Der Bereich kommt aus der Seite, auf der gemeldet wird (bereichAusRoute).
  // Statt einer Auswahlliste fragt der Dialog nur nach: „Geht es um …?“ — erst
  // „Anderen Bereich wählen“ öffnet die Liste.
  // Ein von Hand gewählter Bereich bleibt im Entwurf; sonst gilt die aktuelle Seite.
  const [bereich, setBereich] = React.useState<TicketBereich>(entwurf.bereich ?? kontext?.bereich ?? BEREICH_STANDARD);
  const [bereichWaehlen, setBereichWaehlen] = React.useState(entwurf.bereich !== null);
  const [anhaenge, setAnhaenge] = React.useState<Array<AnhangEntwurf & { vorschau: string }>>(() =>
    entwurf.anhaenge.map((a) => ({ ...a, vorschau: URL.createObjectURL(a.datei) })),
  );
  const [phase, setPhase] = React.useState<Phase>("dialog");
  const [roh, setRoh] = React.useState<HTMLCanvasElement | null>(null);
  // Nach dem Selbstauslöser ohne „Aufnahme …“-Schleier aufnehmen: Der läge
  // sonst unter dem Mauszeiger und schlösse Hover-Tooltips vor dem Bild.
  const [leiseAufnahme, setLeiseAufnahme] = React.useState(false);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [sendet, setSendet] = React.useState(false);
  const [betreffFehlt, setBetreffFehlt] = React.useState(false);
  const betreffRef = React.useRef<HTMLInputElement>(null);
  const dateiRef = React.useRef<HTMLInputElement>(null);

  // Ref hält immer den aktuellen Anhänge-Stand — das Unmount-Cleanup unten
  // läuft nur einmal (leeres Deps-Array) und würde sonst den Stand vom ersten
  // Render einfangen (leeres Array) statt der zuletzt erzeugten Object-URLs.
  const anhaengeRef = React.useRef(anhaenge);
  React.useEffect(() => { anhaengeRef.current = anhaenge; }, [anhaenge]);
  React.useEffect(() => () => anhaengeRef.current.forEach((a) => URL.revokeObjectURL(a.vorschau)), []);

  // Jede Eingabe sofort in den Entwurf — nach erfolgreichem Senden nicht mehr.
  const gesendetRef = React.useRef(false);
  const aktuellerEntwurf = React.useMemo(() => ({
    art, betreff, inhalt,
    bereich: bereichWaehlen ? bereich : null,
    anhaenge: anhaenge.map(({ datei, name, art: a }) => ({ datei, name, art: a })),
  }), [art, betreff, inhalt, bereich, bereichWaehlen, anhaenge]);
  React.useEffect(() => {
    if (!gesendetRef.current) merkeEntwurf(aktuellerEntwurf);
  }, [aktuellerEntwurf]);

  const zuruecksetzen = () => {
    anhaenge.forEach((a) => URL.revokeObjectURL(a.vorschau));
    verwirfEntwurf();
    setArt("problem");
    setBetreff("");
    setInhalt("");
    setBereich(kontext?.bereich ?? BEREICH_STANDARD);
    setBereichWaehlen(false);
    setAnhaenge([]);
    setFehler(null);
    setBetreffFehlt(false);
    betreffRef.current?.focus();
  };

  // Escape schließt den Dialog NUR in der eigentlichen Formular-Phase — während
  // Aufnahme/Markieren aktiv sind, rendern diese ihre eigenen Overlays anstelle
  // des Dialogs, ein hier registrierter Handler würde also ohnehin nicht mehr
  // zusammenpassen. Dennoch als Guard: kein Schließen außerhalb von "dialog".
  React.useEffect(() => {
    if (phase !== "dialog") return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSchliessen();
    };
    alleFenster.addEventListener("keydown", taste);
    return () => alleFenster.removeEventListener("keydown", taste);
  }, [phase, onSchliessen]);

  // Validierung, Fehlermeldung und createObjectURL laufen HIER außerhalb des
  // setAnhaenge-Updaters — unter StrictMode wird der Updater doppelt
  // aufgerufen, ein Seiteneffekt darin (Object-URL, setFehler) liefe also
  // ebenfalls doppelt. Der Updater selbst bleibt eine reine Anhänge-Liste.
  // Die eigentliche Prüfung (Höchstzahl/Höchstgröße, Teil-Annahme) steckt in
  // `anhangRegeln.ts` — geteilt mit TicketDetail (Kommentar/Ergänzung).
  const hinzufuegen = (neu: AnhangEntwurf[]) => {
    const { uebernehmen, meldung } = pruefeAnhaenge(anhaenge.length, neu.map((a) => ({ name: a.name, size: a.datei.size })));
    setFehler(meldung);
    if (uebernehmen.length === 0) return;
    const mitVorschau = uebernehmen.map((i) => ({ ...neu[i], vorschau: URL.createObjectURL(neu[i].datei) }));
    setAnhaenge((alt) => [...alt, ...mitVorschau]);
  };

  const dateienAus = (liste: FileList | File[] | null) =>
    hinzufuegen(Array.from(liste ?? []).map((f) => ({ datei: f, name: f.name || "einfuegen.png", art: "datei" as const })));

  const einfuegen = (e: React.ClipboardEvent) => {
    const dateien = Array.from(e.clipboardData.files);
    if (dateien.length) { e.preventDefault(); dateienAus(dateien); }
  };

  const senden = async () => {
    if (!betreff.trim()) {
      setBetreffFehlt(true);
      betreffRef.current?.focus();
      return;
    }
    setSendet(true);
    setFehler(null);
    try {
      // Telefonie-Stand frisch beim Senden (T-28): wirft nie, höchstens ~1,5 s.
      const telefonie = await sammleTelefonieStand();
      const technik = kontext?.technik ? { ...kontext.technik, spuren: spurenSchnappschuss().spuren } : spurenSchnappschuss();
      const t = await ticketService.anlegen({
        art, betreff: betreff.trim(), inhalt: inhalt.trim(), bereich,
        route: kontext?.route ?? null, seitentitel: kontext?.seitentitel ?? null, ansicht: kontext?.ansicht ?? null,
        bezugTyp: kontext?.bezugTyp ?? null, bezugId: kontext?.bezugId ?? null, bezugLabel: kontext?.bezugLabel ?? null,
        // Spuren frisch beim Senden — so sind auch Fehler aus „Bereich
        // fotografieren“ ([Ticket-Screenshot]) dabei; Fenster/Browser bleiben
        // vom Moment des Meldens.
        technik: telefonie ? { ...technik, telefonie } : technik,
      }, anhaenge.map(({ datei, name, art: a }) => ({ datei, name, art: a })));
      gesendetRef.current = true;
      verwirfEntwurf();
      onGesendet(t.id);
    } catch {
      setFehler("Senden hat nicht geklappt. Deine Eingaben sind noch da, bitte erneut senden.");
    } finally {
      setSendet(false);
    }
  };

  if (phase === "ausloeser") {
    return (
      <Selbstausloeser
        onAusloesen={() => { setLeiseAufnahme(true); setPhase("aufnahme"); }}
        onAbbruch={() => setPhase("dialog")}
      />
    );
  }
  if (phase === "aufnahme") {
    return (
      <BereichAufnahme
        ohneLadeHinweis={leiseAufnahme}
        onFertig={(c) => { setRoh(c); setLeiseAufnahme(false); setPhase("markieren"); }}
        onAbbruch={(msg) => { setFehler(msg ?? null); setLeiseAufnahme(false); setPhase("dialog"); }}
      />
    );
  }
  if (phase === "markieren" && roh) {
    return (
      <MarkierEditor
        bild={roh}
        onFertig={(png) => {
          hinzufuegen([{ datei: png, name: `screenshot-${anhaenge.length + 1}.png`, art: "screenshot" }]);
          setRoh(null);
          setPhase("dialog");
        }}
        onAbbruch={() => { setRoh(null); setPhase("dialog"); }}
      />
    );
  }

  return createPortal(
    <div className="kvp-rd tk-scrim" data-ticket-ignore onMouseDown={(e) => { if (e.target === e.currentTarget) onSchliessen(); }}>
      <aside className="tk-panel" role="dialog" aria-label="Problem oder Idee melden" onPaste={einfuegen}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); dateienAus(e.dataTransfer.files); }}>
        <header className="tk-panel-kopf">
          <h2>{kontext ? "Problem oder Idee melden" : "Neues Ticket"}</h2>
          <button type="button" className="tk-x" aria-label="Schließen" onClick={onSchliessen}>{I.close}</button>
        </header>

        <div className="tk-panel-body">
          <div className="rd-seg" role="group" aria-label="Art">
            {(["problem", "idee"] as const).map((a) => (
              <button key={a} type="button" className={art === a ? "active" : ""} onClick={() => setArt(a)}>
                {a === "problem" ? "Problem" : "Idee"}
              </button>
            ))}
          </div>

          {bereichWaehlen ? (
            <label className="tk-feld">
              <span>Bereich</span>
              <select className="tk-select" value={bereich} onChange={(e) => setBereich(e.target.value as TicketBereich)}>
                {bereichOptionen(bereich).map((b) => <option key={b} value={b}>{bereichAuswahlLabel(b)}</option>)}
              </select>
            </label>
          ) : (
            <p className="tk-bereichsfrage">
              {art === "problem" ? "Hast du wirklich ein Problem im Bereich " : "Hast du wirklich eine Idee zum Bereich "}
              <strong>„{BEREICH_LABEL[bereich]}“</strong>?{" "}
              <button type="button" className="tk-link" onClick={() => setBereichWaehlen(true)}>Nein, Bereich ändern</button>
            </p>
          )}

          <label className={`tk-feld${betreffFehlt ? " tk-feld--fehlt" : ""}`}>
            <span>Betreff<span className="tk-pflicht" aria-hidden="true"> *</span></span>
            <input ref={betreffRef} value={betreff} maxLength={200} required aria-required="true"
              aria-invalid={betreffFehlt || undefined} aria-describedby={betreffFehlt ? "tk-betreff-fehlt" : undefined}
              onChange={(e) => { setBetreff(e.target.value); if (e.target.value.trim()) setBetreffFehlt(false); }} autoFocus />
          </label>
          {betreffFehlt && <p id="tk-betreff-fehlt" className="tk-feld-hinweis" role="alert">Bitte einen Betreff eingeben.</p>}
          <label className="tk-feld">
            <span>Beschreibung</span>
            <textarea rows={5} value={inhalt} onChange={(e) => setInhalt(e.target.value)}
              placeholder={art === "problem" ? "Was hast du gemacht, was ist passiert, was hättest du erwartet?" : "Was soll besser werden?"} />
          </label>

          {/* Route, Bezug und Ansicht werden unsichtbar mitgeschickt. */}

          <div className="tk-anhaenge">
            <div className="tk-anhang-knoepfe">
              <button type="button" className="tp-detail-btn" onClick={() => setPhase("aufnahme")}>{I.image} Bereich fotografieren</button>
              <button type="button" className="tp-detail-btn" onClick={() => setPhase("ausloeser")}
                title="Für offene Menüs, Auswahllisten und Tooltips: erst die Zeit läuft, dann wird fotografiert">
                {I.image} In {SELBSTAUSLOESER_SEKUNDEN} Sekunden fotografieren
              </button>
              <button type="button" className="tp-detail-btn" onClick={() => dateiRef.current?.click()}>{I.plus} Datei anhängen</button>
              <input ref={dateiRef} type="file" multiple hidden onChange={(e) => { dateienAus(e.target.files); e.target.value = ""; }} />
            </div>
            <p className="tk-leise">Mail-Inhalte und PDF-Vorschauen erscheinen hier leer, dafür bitte ‚Datei anhängen‘ nutzen.</p>
            {anhaenge.length > 0 && (
              <ul className="tk-vorschau">
                {anhaenge.map((a, i) => (
                  <li key={a.vorschau}>
                    {a.datei.type.startsWith("image/") ? <img src={a.vorschau} alt={a.name} /> : <span className="tk-datei">{a.name}</span>}
                    <button type="button" aria-label={`${a.name} entfernen`}
                      onClick={() => { URL.revokeObjectURL(a.vorschau); setAnhaenge((x) => x.filter((_, j) => j !== i)); }}>✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {fehler && <p className="tk-fehler" role="alert">{fehler}</p>}
        </div>

        <footer className="tk-panel-fuss">
          <button type="button" className="tp-detail-btn tk-zuruecksetzen" onClick={zuruecksetzen}
            disabled={sendet || istLeer(aktuellerEntwurf)} title="Text, Bilder und Anhänge verwerfen">
            Zurücksetzen
          </button>
          <button type="button" className="tp-detail-btn" onClick={onSchliessen}
            title="Schließen — Text und Bilder bleiben für das nächste Öffnen erhalten">Abbrechen</button>
          <button type="button" className="cd-save" disabled={sendet} onClick={senden}>
            {fehler && !sendet ? "Erneut senden" : "Senden"}
          </button>
        </footer>
      </aside>
    </div>,
    document.body,
  );
}
